import React, { createContext, useState, useCallback, useMemo, useEffect, useReducer, useContext, useRef, useImperativeHandle, forwardRef } from 'react';
import T from 'prop-types';
import Draggable from 'react-draggable';

const calcDims = (width, height, externalMaxWidth, externalMaxHeight) => {
    const ratio = width / height;
    const maxWidth = externalMaxWidth || window.innerWidth;
    const maxHeight = externalMaxHeight || window.innerHeight;
    const calculated = {
        width: maxWidth,
        height: Math.round(maxWidth / ratio),
        ratio: ratio,
    };
    if (calculated.height > maxHeight) {
        calculated.height = maxHeight;
        calculated.width = Math.round(maxHeight * ratio);
    }
    return calculated;
};

function isCrossOriginURL(url) {
    const { location } = window;
    const parts = url.match(/^(\w+:)\/\/([^:/?#]*):?(\d*)/i);
    return (parts !== null &&
        (parts[1] !== location.protocol ||
            parts[2] !== location.hostname ||
            parts[3] !== location.port));
}

/**
 * Takes a file or a string, loads it and turns it into a DataURL
 * @param file as File | string
 * @returns DataURL (as string)
 */
const readFile = (file) => new Promise((resolve, reject) => {
    if (file instanceof File) {
        const reader = new FileReader();
        reader.onload = (_e) => {
            resolve(reader.result);
        };
        reader.onerror = (err) => {
            reject(err);
        };
        reader.readAsDataURL(file);
    }
    else if (typeof file === "string")
        return resolve(file);
    else
        reject();
});

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const applyFilter = (cv, docCanvas, filterCvParams = {}) => __awaiter(void 0, void 0, void 0, function* () {
    // default options
    const options = Object.assign({ blur: false, th: true, thMode: cv.ADAPTIVE_THRESH_MEAN_C, thMeanCorrection: 15, thBlockSize: 25, thMax: 255, grayScale: true }, filterCvParams);
    const dst = cv.imread(docCanvas);
    if (options.grayScale) {
        cv.cvtColor(dst, dst, cv.COLOR_RGBA2GRAY, 0);
    }
    if (options.blur) {
        const ksize = new cv.Size(5, 5);
        cv.GaussianBlur(dst, dst, ksize, 0, 0, cv.BORDER_DEFAULT);
    }
    if (options.th) {
        if (options.grayScale) {
            cv.adaptiveThreshold(dst, dst, options.thMax, options.thMode, cv.THRESH_BINARY, options.thBlockSize, options.thMeanCorrection);
        }
        else {
            dst.convertTo(dst, -1, 1, 60);
            cv.threshold(dst, dst, 170, 255, cv.THRESH_BINARY);
        }
    }
    cv.imshow(docCanvas, dst);
});

/**
 * perspective cropping utility (AKA keystone correction)
 * @param cv openCv
 * @param docCanvas
 * @param cropPoints
 * @param imageResizeRatio
 * @param setPreviewPaneDimensions
 */
const warpPerspective = (cv, docCanvas, cropPoints, imageResizeRatio, setPreviewPaneDimensions) => {
    const dst = cv.imread(docCanvas);
    const bR = cropPoints["right-bottom"];
    const bL = cropPoints["left-bottom"];
    const tR = cropPoints["right-top"];
    const tL = cropPoints["left-top"];
    // create source coordinates matrix
    const sourceCoordinates = [tL, tR, bR, bL].map((point) => [
        point.x / imageResizeRatio,
        point.y / imageResizeRatio,
    ]);
    // get max width
    const maxWidth = Math.max(bR.x - bL.x, tR.x - tL.x) / imageResizeRatio;
    // get max height
    const maxHeight = Math.max(bL.y - tL.y, bR.y - tR.y) / imageResizeRatio;
    // create dest coordinates matrix
    const destCoordinates = [
        [0, 0],
        [maxWidth - 1, 0],
        [maxWidth - 1, maxHeight - 1],
        [0, maxHeight - 1],
    ];
    // convert to open cv matrix objects
    const Ms = cv.matFromArray(4, 1, cv.CV_32FC2, [].concat(...sourceCoordinates));
    const Md = cv.matFromArray(4, 1, cv.CV_32FC2, [].concat(...destCoordinates));
    const transformMatrix = cv.getPerspectiveTransform(Ms, Md);
    // set new image size
    const dsize = new cv.Size(maxWidth, maxHeight);
    // perform warp
    cv.warpPerspective(dst, dst, transformMatrix, dsize, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
    cv.imshow(docCanvas, dst);
    dst.delete();
    Ms.delete();
    Md.delete();
    transformMatrix.delete();
    setPreviewPaneDimensions();
};

function multiplyMatrices(m1, m2) {
    var result = [];
    for (var i = 0; i < m1.length; i++) {
        result[i] = [];
        for (var j = 0; j < m2[0].length; j++) {
            var sum = 0;
            for (var k = 0; k < m1[0].length; k++) {
                sum += m1[i][k] * m2[k][j];
            }
            result[i][j] = sum;
        }
    }
    return result;
}

const cropperReducer = (state, action) => {
    switch (action.type) {
        case "JOIN_PAYLOAD":
            return Object.assign(Object.assign({}, state), action.payload);
        case "MODE":
            if (state.mode === action.payload)
                return Object.assign(Object.assign({}, state), { mode: null });
            return Object.assign(Object.assign({}, state), { mode: action.payload });
        default:
            return Object.assign({}, state);
    }
};

const initialRomaineState = {
    mode: null,
};

const moduleConfig = {
    wasmBinaryFile: "opencv_js.wasm",
    usingWasm: true,
};

const OpenCvContext = createContext({
    loaded: false,
    romaine: initialRomaineState,
});
const { Consumer: OpenCvConsumer, Provider } = OpenCvContext;
const scriptId = "openCvScriptTag";
/**
 * a romaine context for use in getting openCV and the canvas ref element
 * @todo
 * 1) remove ts-ignore
 * 2) Add ref to provider
 */
const Romaine = ({ openCvPath, children, onLoad }) => {
    const [loaded, setLoaded] = useState(false);
    const handleOnLoad = useCallback(() => {
        console.log(window.cv);
        if (onLoad) {
            onLoad(window.cv);
        }
        setLoaded(true);
    }, [onLoad]);
    const generateOpenCvScriptTag = useMemo(() => {
        const js = document.createElement("script");
        js.id = scriptId;
        js.src = openCvPath || "https://docs.opencv.org/3.4.13/opencv.js";
        //@ts-ignore
        js.nonce = true;
        js.defer = true;
        js.async = true;
        return js;
    }, [openCvPath]);
    useEffect(() => {
        if (document.getElementById(scriptId) || window.cv) {
            setLoaded(true);
            return;
        }
        // https://docs.opencv.org/3.4/dc/de6/tutorial_js_nodejs.html
        // https://medium.com/code-divoire/integrating-opencv-js-with-an-angular-application-20ae11c7e217
        // https://stackoverflow.com/questions/56671436/cv-mat-is-not-a-constructor-opencv
        moduleConfig.onRuntimeInitialized = handleOnLoad;
        window.Module = moduleConfig;
        // if (!document.getElementById(scriptId))
        document.body.appendChild(generateOpenCvScriptTag);
        // else handleOnLoad();
    }, [openCvPath, handleOnLoad]);
    const [romaine, dispatchRomaine] = useReducer(cropperReducer, initialRomaineState);
    const setMode = useCallback((mode) => {
        dispatchRomaine({ type: "MODE", payload: mode });
    }, [dispatchRomaine, cropperReducer]);
    const memoizedProviderValue = useMemo(() => ({ loaded, cv: window.cv, romaine, setMode }), [loaded, romaine]);
    return React.createElement(Provider, { value: memoizedProviderValue }, children);
};
Romaine.propTypes = {
    openCvPath: T.string,
    children: T.node,
    onLoad: T.func,
};

const useRomaine = () => useContext(OpenCvContext);

/**
 * Add default styles to the points
 * Makes sure position absolute cannot be overwritten
 * @param cropPointStyles
 */
const buildCropPointStyle = (cropPointStyles = {}) => (Object.assign(Object.assign({ backgroundColor: "transparent", border: "4px solid #3cabe2", zIndex: 1001, borderRadius: "100%" }, cropPointStyles), { position: "absolute" }));
/**
 * @returns A crop point to use during cropping and image rotation
 */
const CropPoint = ({ pointSize, cropPoints, pointArea, defaultPosition, onStop: externalOnStop, onDrag: externalOnDrag, bounds, cropPointStyles = {}, }) => {
    const cropPointStyle = useMemo(() => {
        if (cropPointStyles.width !== pointSize ||
            cropPointStyles.height !== pointSize) {
            console.warn("");
        }
        cropPointStyles.width = pointSize;
        cropPointStyles.height = pointSize;
        return buildCropPointStyle(cropPointStyles);
    }, [cropPointStyles, pointSize]);
    const onDrag = useCallback((_, position) => {
        externalOnDrag(Object.assign(Object.assign({}, position), { x: position.x + pointSize / 2, y: position.y + pointSize / 2 }), pointArea);
    }, [externalOnDrag]);
    const onStop = useCallback((_, position) => {
        externalOnStop(Object.assign(Object.assign({}, position), { x: position.x + pointSize / 2, y: position.y + pointSize / 2 }), pointArea, cropPoints);
    }, [externalOnDrag, cropPoints]);
    return (React.createElement(Draggable, { bounds: bounds, defaultPosition: defaultPosition, position: {
            x: cropPoints[pointArea].x - pointSize / 2,
            y: cropPoints[pointArea].y - pointSize / 2,
        }, onDrag: onDrag, onStop: onStop },
        React.createElement("div", { style: cropPointStyle })));
};
CropPoint.propTypes = {
    cropPoints: T.shape({
        "left-top": T.shape({ x: T.number, y: T.number }).isRequired,
        "right-top": T.shape({ x: T.number, y: T.number }).isRequired,
        "right-bottom": T.shape({ x: T.number, y: T.number }).isRequired,
        "left-bottom": T.shape({ x: T.number, y: T.number }).isRequired,
    }),
    pointArea: T.oneOf(["left-top", "right-top", "right-bottom", "left-bottom"]),
    defaultPosition: T.shape({
        x: T.number,
        y: T.number,
    }),
    onStop: T.func,
    onDrag: T.func,
    cropPointStyles: T.object,
};

const CropPoints = (_a) => {
    var { previewDims } = _a, otherProps = __rest(_a, ["previewDims"]);
    return (React.createElement(React.Fragment, null,
        React.createElement(CropPoint, Object.assign({ pointArea: "left-top", defaultPosition: { x: 0, y: 0 } }, otherProps)),
        React.createElement(CropPoint, Object.assign({ pointArea: "right-top", defaultPosition: { x: previewDims.width, y: 0 } }, otherProps)),
        React.createElement(CropPoint, Object.assign({ pointArea: "right-bottom", defaultPosition: { x: 0, y: previewDims.height } }, otherProps)),
        React.createElement(CropPoint, Object.assign({ pointArea: "left-bottom", defaultPosition: {
                x: previewDims.width,
                y: previewDims.height,
            } }, otherProps))));
};
CropPoints.propTypes = {
    previewDims: T.shape({
        ratio: T.number,
        width: T.number,
        height: T.number,
    }),
};

/**
 * Create the lines for the cropper utility
 */
const CropPointsDelimiters = (_a) => {
    var { romaineRef, cropPoints, previewDims, lineWidth = 3, lineColor = "#3cabe2", pointSize } = _a, props = __rest(_a, ["romaineRef", "cropPoints", "previewDims", "lineWidth", "lineColor", "pointSize"]);
    const canvas = useRef(null);
    const clearCanvas = useCallback(() => {
        if (canvas.current) {
            const ctx = canvas.current.getContext("2d");
            ctx && ctx.clearRect(0, 0, previewDims.width, previewDims.height);
        }
    }, [canvas.current, previewDims]);
    const sortPoints = useCallback(() => {
        const sortOrder = [
            "left-top",
            "right-top",
            "right-bottom",
            "left-bottom",
        ];
        return sortOrder.reduce((acc, pointPos) => [...acc, cropPoints[pointPos]], []);
    }, [cropPoints]);
    const drawShape = useCallback(([point1, point2, point3, point4]) => {
        const ctx = canvas.current && canvas.current.getContext("2d");
        if (ctx) {
            ctx.lineWidth = lineWidth;
            ctx.strokeStyle = lineColor;
            ctx.beginPath();
            ctx.moveTo(point1.x + pointSize / 2, point1.y);
            ctx.lineTo(point2.x - pointSize / 2, point2.y);
            ctx.moveTo(point2.x, point2.y + pointSize / 2);
            ctx.lineTo(point3.x, point3.y - pointSize / 2);
            ctx.moveTo(point3.x - pointSize / 2, point3.y);
            ctx.lineTo(point4.x + pointSize / 2, point4.y);
            ctx.moveTo(point4.x, point4.y - pointSize / 2);
            ctx.lineTo(point1.x, point1.y + pointSize / 2);
            ctx.closePath();
            ctx.stroke();
        }
    }, [canvas.current]);
    useEffect(() => {
        if (cropPoints && canvas.current) {
            clearCanvas();
            const sortedPoints = sortPoints();
            drawShape(sortedPoints);
        }
    }, [cropPoints, canvas.current]);
    /**
     * Takes in a `CoordinateXY` and makes sure that it is inside the `ContourCoordinates`
     *
     * @returns boolean
     *
     * @todo Should use point slope formula for when using perspective cropper
     */
    const xyInPoints = ({ x, y }) => {
        if (x > cropPoints["left-top"].x &&
            y > cropPoints["left-top"].y &&
            x < cropPoints["right-top"].x &&
            y > cropPoints["right-top"].y &&
            x < cropPoints["right-bottom"].x &&
            y < cropPoints["right-bottom"].y &&
            x > cropPoints["left-bottom"].x &&
            y < cropPoints["left-bottom"].y)
            return true;
        return false;
    };
    const getCursorPosition = (event) => {
        var _a, _b, _c;
        if ((_a = canvas === null || canvas === void 0 ? void 0 : canvas.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect) {
            const x = event.clientX - ((_b = canvas === null || canvas === void 0 ? void 0 : canvas.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().left);
            const y = event.clientY - ((_c = canvas === null || canvas === void 0 ? void 0 : canvas.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().top);
            if (xyInPoints({ x, y }))
                return "inside";
            else
                return "outside";
        }
    };
    const [cursor, setCursor] = useState("default");
    const handleMouseMove = (e) => {
        const cursorPosition = getCursorPosition(e);
        if (cursorPosition === "inside" && cursor !== "crosshair")
            setCursor("crosshair");
        else if (cursorPosition === "outside" && cursor !== "default")
            setCursor("default");
    };
    const handleClick = (e) => {
        var _a;
        const cursorPosition = getCursorPosition(e);
        if (cursorPosition === "inside" && ((_a = romaineRef === null || romaineRef === void 0 ? void 0 : romaineRef.current) === null || _a === void 0 ? void 0 : _a.crop))
            romaineRef.current.crop({
                preview: true,
                filterCvParams: {
                    grayScale: false,
                    th: false,
                },
                image: {
                    quality: 0.92,
                    type: "image/jpeg",
                },
            });
    };
    return (React.createElement("canvas", { id: `${props.saltId ? props.saltId + "-" : ""}crop-point-delimiters`, ref: canvas, style: {
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            zIndex: 5,
            cursor,
        }, width: previewDims.width, height: previewDims.height, onClick: handleClick, onMouseMove: handleMouseMove, onMouseLeave: () => setCursor("default") }));
};
CropPointsDelimiters.propTypes = {
    previewDims: T.shape({
        ratio: T.number,
        width: T.number,
        height: T.number,
    }),
    cropPoints: T.shape({
        "left-top": T.shape({ x: T.number, y: T.number }).isRequired,
        "right-top": T.shape({ x: T.number, y: T.number }).isRequired,
        "right-bottom": T.shape({ x: T.number, y: T.number }).isRequired,
        "left-bottom": T.shape({ x: T.number, y: T.number }).isRequired,
    }),
    lineColor: T.string,
    lineWidth: T.number,
    pointSize: T.number,
};

const CroppingCanvas = ({ image, onDragStop, onChange, romaineRef, previewCanvasRef, canvasRef, pointSize = 30, lineWidth, lineColor, 
// maxWidth,
// maxHeight,
previewDims, 
// setPreviewDims,
imageResizeRatio, showPreview, 
// createCanvas,
setPreviewPaneDimensions, saltId, }) => {
    var _a, _b, _c, _d, _e, _f;
    const { loaded: cvLoaded, cv, romaine, setMode: changeMode } = useRomaine();
    // let canvasRef = useRef<HTMLCanvasElement>();
    // const magnifierCanvasRef = useRef<HTMLCanvasElement>(null);
    const [cropPoints, setCropPoints] = useState();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState("crop");
    useImperativeHandle(romaineRef, () => ({
        backToCrop: () => {
            setMode("crop");
        },
        crop: (opts = {}) => __awaiter(void 0, void 0, void 0, function* () {
            return new Promise((resolve) => {
                var _a, _b;
                if (canvasRef.current && cropPoints) {
                    setLoading(true);
                    warpPerspective(cv, canvasRef.current, cropPoints, imageResizeRatio, setPreviewPaneDimensions);
                    applyFilter(cv, canvasRef.current, opts.filterCvParams);
                    if (opts.preview) {
                        showPreview(imageResizeRatio);
                    }
                    // @todo
                    // make sure that this is still necessary
                    canvasRef.current.toBlob((blob) => {
                        // blob.name = image.name;
                        resolve(blob);
                        setLoading(false);
                        changeMode && changeMode(null);
                    }, ((_a = opts === null || opts === void 0 ? void 0 : opts.image) === null || _a === void 0 ? void 0 : _a.type) ||
                        (typeof image !== "string" ? image.type : "image/png"), ((_b = opts === null || opts === void 0 ? void 0 : opts.image) === null || _b === void 0 ? void 0 : _b.quality) || 1);
                }
            });
        }),
    }));
    const detectContours = () => __awaiter(void 0, void 0, void 0, function* () {
        const dst = cv.imread(canvasRef.current);
        const ksize = new cv.Size(5, 5);
        // convert the image to grayscale, blur it, and find edges in the image
        cv.cvtColor(dst, dst, cv.COLOR_RGBA2GRAY, 0);
        cv.GaussianBlur(dst, dst, ksize, 0, 0, cv.BORDER_DEFAULT);
        cv.Canny(dst, dst, 75, 200);
        // find contours
        cv.threshold(dst, dst, 120, 200, cv.THRESH_BINARY);
        const contours = new cv.MatVector();
        const hierarchy = new cv.Mat();
        cv.findContours(dst, contours, hierarchy, cv.RETR_CCOMP, cv.CHAIN_APPROX_SIMPLE);
        const rect = cv.boundingRect(dst);
        dst.delete();
        hierarchy.delete();
        contours.delete();
        // transform the rectangle into a set of points
        Object.keys(rect).forEach((key) => {
            rect[key] = rect[key] * imageResizeRatio;
        });
        const contourCoordinates = {
            "left-top": { x: rect.x, y: rect.y },
            "right-top": { x: rect.x + rect.width, y: rect.y },
            "right-bottom": {
                x: rect.x + rect.width,
                y: rect.y + rect.height,
            },
            "left-bottom": { x: rect.x, y: rect.y + rect.height },
        };
        setCropPoints(contourCoordinates);
    });
    // const clearCanvasByRef = (ref: React.RefObject<HTMLCanvasElement>) => {
    //   if (ref.current) {
    //     const magnCtx = ref.current.getContext("2d");
    //     magnCtx && magnCtx.clearRect(0, 0, ref.current.width, ref.current.height);
    //   }
    // };
    useEffect(() => {
        if (onChange && cropPoints) {
            onChange(Object.assign(Object.assign({}, cropPoints), { loading }));
        }
    }, [cropPoints, loading]);
    const bootstrap = () => __awaiter(void 0, void 0, void 0, function* () {
        detectContours().then(() => setLoading(false));
    });
    useEffect(() => {
        if (image && previewCanvasRef.current && cvLoaded && mode === "crop") {
            bootstrap();
        }
        else {
            setLoading(true);
        }
    }, [image, previewCanvasRef.current, cvLoaded, mode]);
    const handleNormalCornerMove = useCallback((position, area, cPs) => {
        const { x, y } = position;
        if (cPs) {
            switch (area) {
                case "left-bottom":
                    return Object.assign(Object.assign({}, cPs), { [area]: { x, y }, "left-top": { x, y: cPs["left-top"].y }, "right-bottom": { x: cPs["right-bottom"].x, y } });
                case "right-bottom":
                    return Object.assign(Object.assign({}, cPs), { [area]: { x, y }, "right-top": { x, y: cPs["right-top"].y }, "left-bottom": { x: cPs["left-bottom"].x, y } });
                case "right-top":
                    return Object.assign(Object.assign({}, cPs), { [area]: { x, y }, "left-top": { x: cPs["left-top"].x, y }, "right-bottom": { x, y: cPs["right-bottom"].y } });
                case "left-top":
                    return Object.assign(Object.assign({}, cPs), { [area]: { x, y }, "left-bottom": { x, y: cPs["left-bottom"].y }, "right-top": { x: cPs["right-top"].x, y } });
            }
        }
    }, []);
    const onCornerDrag = useCallback((position, area) => {
        // if (magnifierCanvasRef.current && previewCanvasRef.current) {
        // const { x, y } = position;
        // const magnCtx = magnifierCanvasRef.current.getContext("2d");
        // clearCanvasByRef(magnifierCanvasRef);
        // TODO we should make those 5, 10 and 20 values proportionate
        // to the point size
        // magnCtx &&
        //   magnCtx.drawImage(
        //     previewCanvasRef.current,
        //     x - (pointSize - 10),
        //     y - (pointSize - 10),
        //     pointSize + 5,
        //     pointSize + 5,
        //     x + 10,
        //     y - 90,
        //     pointSize + 20,
        //     pointSize + 20
        //   );
        setCropPoints((cPs) => {
            if (cPs && romaine.mode === "perspective-crop")
                return Object.assign(Object.assign({}, cPs), { [area]: position });
            return handleNormalCornerMove(position, area, cPs);
        });
        // }
    }, [romaine.mode]);
    const onCornerStop = useCallback((position, area, cropPoints) => {
        const { x, y } = position;
        // clearCanvasByRef(magnifierCanvasRef);
        setCropPoints((cPs) => {
            if (cPs && romaine.mode === "perspective-crop")
                return Object.assign(Object.assign({}, cPs), { [area]: { x, y } });
            return handleNormalCornerMove(position, area, cPs);
        });
        if (onDragStop) {
            onDragStop(Object.assign(Object.assign({}, cropPoints), { [area]: { x, y }, loading }));
        }
    }, [romaine.mode]);
    return (React.createElement(React.Fragment, null, previewDims && mode === "crop" && cropPoints && (React.createElement(React.Fragment, null,
        React.createElement(CropPoints, { pointSize: pointSize, cropPoints: cropPoints, previewDims: previewDims, onDrag: onCornerDrag, onStop: onCornerStop, bounds: {
                left: (((_a = previewCanvasRef === null || previewCanvasRef === void 0 ? void 0 : previewCanvasRef.current) === null || _a === void 0 ? void 0 : _a.offsetLeft) || 0) - pointSize / 2,
                top: (((_b = previewCanvasRef === null || previewCanvasRef === void 0 ? void 0 : previewCanvasRef.current) === null || _b === void 0 ? void 0 : _b.offsetTop) || 0) - pointSize / 2,
                right: (((_c = previewCanvasRef === null || previewCanvasRef === void 0 ? void 0 : previewCanvasRef.current) === null || _c === void 0 ? void 0 : _c.offsetLeft) || 0) -
                    pointSize / 2 +
                    (((_d = previewCanvasRef === null || previewCanvasRef === void 0 ? void 0 : previewCanvasRef.current) === null || _d === void 0 ? void 0 : _d.offsetWidth) || 0),
                bottom: (((_e = previewCanvasRef === null || previewCanvasRef === void 0 ? void 0 : previewCanvasRef.current) === null || _e === void 0 ? void 0 : _e.offsetTop) || 0) -
                    pointSize / 2 +
                    (((_f = previewCanvasRef === null || previewCanvasRef === void 0 ? void 0 : previewCanvasRef.current) === null || _f === void 0 ? void 0 : _f.offsetHeight) || 0),
            } }),
        React.createElement(CropPointsDelimiters, { romaineRef: romaineRef, previewDims: previewDims, cropPoints: cropPoints, lineWidth: lineWidth, lineColor: lineColor, pointSize: pointSize, saltId: saltId })))));
};
CroppingCanvas.propTypes = {
    image: T.object.isRequired,
    onDragStop: T.func,
    onChange: T.func,
    cropperRef: T.shape({
        current: T.shape({
            done: T.func.isRequired,
            backToCrop: T.func.isRequired,
        }),
    }),
    pointSize: T.number,
    lineWidth: T.number,
    pointBgColor: T.string,
    pointBorder: T.string,
    lineColor: T.string,
};

const buildImgContainerStyle = (previewDims) => ({
    width: previewDims.width,
    height: previewDims.height,
});

let imageResizeRatio = 1;
const CanvasActual = (_a) => {
    var { romaineRef } = _a, props = __rest(_a, ["romaineRef"]);
    const { cv, romaine: { mode }, setMode, } = useRomaine();
    useImperativeHandle(romaineRef, () => ({
        getBlob: (opts = {}) => __awaiter(void 0, void 0, void 0, function* () {
            return new Promise((resolve) => {
                if (canvasRef.current) {
                    canvasRef.current.toBlob((blob) => {
                        // blob.name = image.name;
                        resolve(blob);
                        setLoading(false);
                        setMode && setMode(null);
                    }, (opts === null || opts === void 0 ? void 0 : opts.type) ||
                        (typeof image !== "string" ? image.type : "image/png"), (opts === null || opts === void 0 ? void 0 : opts.quality) || 1);
                }
            });
        }),
    }));
    const { maxHeight, maxWidth, image } = props;
    const previewCanvasRef = useRef(null);
    const canvasRef = useRef();
    const [previewDims, setPreviewDims] = useState({
        height: maxHeight,
        width: maxWidth,
        ratio: 1,
    });
    const [loading, setLoading] = useState(true);
    const [originalDims, setOriginalDims] = useState({ height: 0, width: 0 });
    const setPreviewPaneDimensions = (dims = originalDims) => {
        if (dims && (previewCanvasRef === null || previewCanvasRef === void 0 ? void 0 : previewCanvasRef.current)) {
            let newPreviewDims = calcDims(dims.width, dims.height, maxWidth, maxHeight);
            setPreviewDims(newPreviewDims);
            previewCanvasRef.current.width = newPreviewDims.width;
            previewCanvasRef.current.height = newPreviewDims.height;
            imageResizeRatio = newPreviewDims.width / dims.width;
        }
    };
    /**
     *
     * @param imageResizeRatio maxWidth / width
     * @param src (optional) The openCV imread pointer defaults to `cv.imread(canvasRef.current)`
     * @param cleanup (default is true) Should the src object be cleaned up
     * only use false if cleaning up your own src object! Otherwise this will result in memory leak!
     */
    const showPreview = (imageResizeRatio, source = cv.imread(canvasRef.current), cleanup = true) => {
        if (cv) {
            const dst = new cv.Mat();
            const dsize = new cv.Size(0, 0);
            cv.resize(source, dst, dsize, imageResizeRatio, imageResizeRatio, cv.INTER_AREA);
            cv.imshow(previewCanvasRef.current, dst);
            if (cleanup)
                source.delete();
            dst.delete();
        }
    };
    const createCanvas = (src) => {
        return new Promise((resolve, reject) => {
            try {
                const img = document.createElement("img");
                img.onload = () => __awaiter(void 0, void 0, void 0, function* () {
                    // set edited image canvas and dimensions
                    canvasRef.current = document.createElement("canvas");
                    canvasRef.current.id = `${props.saltId ? props.saltId + "-" : ""}working-canvas`;
                    canvasRef.current.width = img.width;
                    canvasRef.current.height = img.height;
                    setOriginalDims({ height: img.height, width: img.width });
                    const ctx = canvasRef.current.getContext("2d");
                    if (ctx) {
                        ctx.fillStyle = "#fff0";
                        ctx.fillRect(0, 0, img.width, img.height);
                        ctx.drawImage(img, 0, 0);
                        setPreviewPaneDimensions({ height: img.height, width: img.width });
                        return resolve();
                    }
                    return reject();
                });
                if (isCrossOriginURL(src))
                    img.crossOrigin = "anonymous";
                img.src = src;
            }
            catch (err) {
                reject();
            }
        });
    };
    useEffect(() => {
        setLoading(true);
        readFile(image).then((res) => __awaiter(void 0, void 0, void 0, function* () {
            yield createCanvas(res);
            cv && showPreview(imageResizeRatio);
            console.log(cv);
            setLoading(false);
        }));
    }, [cv, image]);
    const rotate_bound = (canvas, angle) => {
        const src = cv.imread(canvasRef.current);
        const dst = new cv.Mat();
        const center = new cv.Point(src.cols / 2, src.rows / 2);
        const M1_temp = cv.getRotationMatrix2D(center, angle, 1);
        const a = [...M1_temp.data64F];
        M1_temp.delete();
        const cos = Math.abs(a[0]);
        const sin = Math.abs(a[3]);
        // compute the new bounding dimensions of the image
        const newWidth = ~~(src.rows * sin + src.cols * cos);
        const newHeight = ~~(src.rows * cos + src.cols * sin);
        /**
         * Col 3 Row 1 is horizontal transform (numerical position away from y axis)
         *
         * Col 3 Row 2 is vertical transform ("" y axis)
         *
         * @description
         * This code is a modified version of rotate_bound found in python package imutils
         * @link
         * https://github.com/jrosebr1/imutils/blob/c12f15391fcc945d0d644b85194b8c044a392e0a/imutils/convenience.py#L41
         */
        const M1 = [
            [a[0], a[1], a[2] + newWidth / 2 - center.x],
            [a[3], a[4], a[5] + newHeight / 2 - center.y],
        ];
        const oneDimensionalArray = [].concat.apply([], M1);
        const M = cv.matFromArray(2, 3, cv.CV_64FC1, oneDimensionalArray);
        canvas.height = newHeight;
        canvas.width = newWidth;
        setPreviewPaneDimensions({ height: newHeight, width: newWidth });
        // this is the slowest step
        cv.warpAffine(src, dst, M, { height: newHeight, width: newWidth }, cv.INTER_LINEAR, cv.BORDER_CONSTANT, new cv.Scalar());
        M.delete();
        src.delete();
        setTimeout(() => {
            // show the real preview first so it works faster for user
            // due to this we must cleanup dst ourselves
            showPreview(imageResizeRatio, dst, false);
            cv.imshow(canvas, dst);
            dst.delete();
            // finished, set the mode back to null
            setMode && setMode(null);
        }, 0);
    };
    // opencv documentation
    // https://docs.opencv.org/3.4/dd/d52/tutorial_js_geometric_transformations.html
    useEffect(() => {
        if (canvasRef.current) {
            if (mode === "rotate-left") {
                const angle = 1;
                rotate_bound(canvasRef.current, angle);
            }
            else if (mode === "rotate-right") {
                const angle = 359;
                rotate_bound(canvasRef.current, angle);
            }
        }
    }, [mode]);
    return (React.createElement("div", { style: Object.assign({ position: "relative" }, (previewDims && buildImgContainerStyle(previewDims))) },
        React.createElement("canvas", { id: `${props.saltId ? props.saltId + "-" : ""}preview-canvas`, style: {
                backgroundSize: "20px 20px",
                backgroundImage: "linear-gradient(to bottom, #0001 10px, #0003 10px),linear-gradient(to right, #0002 10px, #0004 10px),linear-gradient(to right, transparent 10px, #ffff 10px),linear-gradient(to bottom, #0004 10px, transparent 10px),linear-gradient(to bottom, #ffff 10px, #ffff 10px)",
                position: "absolute",
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                zIndex: 5,
                pointerEvents: "none",
            }, ref: previewCanvasRef, width: maxWidth, height: maxHeight }),
        (mode === "crop" || mode === "perspective-crop") && !loading && (React.createElement(CroppingCanvas, Object.assign({ romaineRef: romaineRef, imageResizeRatio: imageResizeRatio, setPreviewPaneDimensions: setPreviewPaneDimensions, createCanvas: createCanvas, showPreview: showPreview, canvasRef: canvasRef, previewCanvasRef: previewCanvasRef, previewDims: previewDims, setPreviewDims: setPreviewDims }, props)))));
};
/**
 * This function is to make sure the the user is using Romaine context
 *
 * Can also pass children that can be absolutely positioned
 */
const Canvas = forwardRef((_a, ref) => {
    var { openCvPath, children, image, wrapperProps = {} } = _a, props = __rest(_a, ["openCvPath", "children", "image", "wrapperProps"]);
    const { cv, loaded } = useRomaine();
    return cv || loaded ? (React.createElement("div", Object.assign({ id: `${props.saltId ? props.saltId + "-" : ""}romaine-wrapper` }, wrapperProps, { style: Object.assign({ position: "relative", display: "grid", placeItems: "center", padding: "0 250px 0 2em", width: props.maxWidth, height: props.maxHeight, marginLeft: "auto", marginRight: "auto" }, wrapperProps.style) }),
        children,
        image && cv && (React.createElement(CanvasActual, Object.assign({ image: image }, props, { romaineRef: ref }))))) : null;
});

export { Canvas, CropPoint, CropPoints, CropPointsDelimiters, CroppingCanvas, OpenCvConsumer, OpenCvContext, Romaine, applyFilter, calcDims, cropperReducer, initialRomaineState, isCrossOriginURL, multiplyMatrices, readFile, useRomaine, warpPerspective };
//# sourceMappingURL=romaine.development.esm.jsx.map
